/*!
=========================================================
* Argon Dashboard PRO React - v1.2.5
=========================================================
* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)
* Coded by Creative Tim
=========================================================
* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { fetchDirectories, getSite, deleteDirectory, getDirectoryTenantCounts, getDirectoryDevices, getClient, deleteUserDir, deleteDeviceDir } from "./directoryService";
import { SyncDeviceModal } from "../devices/deviceService";
import { hasPermission, PERMISSIONS } from "utils/permissions";
import { Spinner } from "react-bootstrap";
import { serverHost } from "variables/serverdata";

// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  Table,
  Container,
  Row,
  Col,
  Media,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  InputGroup,
  InputGroupText,
} from "reactstrap";

import HusariaHeader from "views/pages/components/HusariaHeader";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import { ThemeContext } from "contexts/ThemeContext.jsx";

// core components
const { SearchBar } = Search;

export function getAuthToken() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return localStorage.getItem("token");
    const parsed = JSON.parse(raw);
    return parsed?.access_token || parsed?.token || parsed?.accessToken || null;
  } catch (e) {
    console.error("Failed to parse localStorage user:", e);
    return localStorage.getItem("token");
  }
}

const pagination = paginationFactory({
  page: 1,
  alwaysShowAllBtns: true,
  showTotal: true,
  withFirstAndLast: false,
  sizePerPageRenderer: ({ options, currSizePerPage, onSizePerPageChange }) => (
    <div className="dataTables_length" id="datatable-basic_length">
      <label>
        Show{" "}
        {
          <select
            name="datatable-basic_length"
            aria-controls="datatable-basic"
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        }{" "}
        entries.
      </label>
    </div>
  ),
});

function Directories() {
  const navigate = useNavigate();
  const [directories, setDirectories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteId, setSiteId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteDirectoryId, setDeleteDirectoryId] = useState(null);
  const [syncModal, setSyncModal] = useState(false);
  const [directoryToSync, setDirectoryToSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncLabel, setSyncLabel] = useState("Syncing devices…");
  const [deleting, setDeleting] = useState(false);
  const [deleteLabel, setDeleteLabel] = useState("Deleting directory…");
  const { theme } = useContext(ThemeContext);
  const [selectedDirUuids, setSelectedDirUuids] = useState([]);
  const [bulkSyncModal, setBulkSyncModal] = useState(false);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const isDark = theme === "dark";

  const userData = React.useMemo(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  }, []);

  const canCreate = hasPermission(userData, PERMISSIONS.CREATE);
  const canEdit = hasPermission(userData, PERMISSIONS.EDIT);
  const canDelete = hasPermission(userData, PERMISSIONS.DELETE);

  const canPerformActions = canCreate || canEdit || canDelete;

  useEffect(() => {
    setSelectedDirUuids([]);
  }, [siteId]);

  // Initialize siteId using helper
  useEffect(() => {
    const { site_uuid } = getSite();
    setSiteId(site_uuid || null);
  }, []);

  // Fetch directories with tenant counts and devices when siteId changes
  useEffect(() => {
    const loadDirectories = async () => {
      if (!siteId) {
        setDirectories([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch tenant counts and devices in parallel
        const [tenantCounts, deviceData] = await Promise.all([
          getDirectoryTenantCounts(siteId),
          getDirectoryDevices(siteId),
        ]);

        // Map directories by dir_uuid to merge data
        const directoriesMap = {};

        tenantCounts.forEach((dir) => {
          directoriesMap[dir.dir_uuid] = {
            ...dir,
            devices: [], // will fill next
          };
        });

        deviceData.forEach((dir) => {
          if (directoriesMap[dir.dir_uuid]) {
            directoriesMap[dir.dir_uuid].devices =
              (dir.devices || []).filter(d => d.device_uuid);
          }
        });

        setDirectories(Object.values(directoriesMap));
      } catch (error) {
        console.error("Error fetching directories:", error);
        setDirectories([]);
      } finally {
        setLoading(false);
      }
    };

    loadDirectories();
  }, [siteId]);

  const openDeleteModal = (dirId) => {
    setDeleteDirectoryId(dirId);
    setDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteDirectoryId(null);
    setDeleteModal(false);
  };

  const UNKNOWN_DEVICE_SVG =
    "data:image/svg+xml;utf8," +
    encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
      <rect width="64" height="64" rx="10" fill="#edf2f7"/>
      <rect x="18" y="10" width="28" height="44" rx="4" fill="#cbd5e0"/>
      <circle cx="32" cy="46" r="3" fill="#718096"/>
      <rect x="24" y="16" width="16" height="16" rx="2" fill="#a0aec0"/>
      <rect x="26" y="20" width="12" height="2" fill="#718096"/>
      <rect x="26" y="24" width="12" height="2" fill="#718096"/>
      <rect x="26" y="28" width="12" height="2" fill="#718096"/>
    </svg>
  `);

  const handleDelete = async () => {
    if (!deleteDirectoryId) return;

    setDeleteModal(false);
    setDeleting(true);

    const MIN_DURATION = 5000; // 5 seconds
    const startTime = Date.now();

    try {
      const response = await deleteDirectory(deleteDirectoryId, siteId);

      if (response.error) {
        alert("Error deleting directory: " + response.error);
        return;
      }

      setDirectories((dirs) =>
        dirs.filter((dir) => dir.dir_uuid !== deleteDirectoryId)
      );

      closeDeleteModal();

    } catch (err) {
      alert("Error deleting directory: " + err.message);
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(MIN_DURATION - elapsed, 0);

      setTimeout(() => {
        setDeleting(false);
      }, remaining);
    }
  };

  const toggleSyncModal = (directory = null) => {
    setDirectoryToSync(directory);
    setSyncModal(!syncModal);
  };

  const handleSync = async (directory) => {
    if (!directory) return;

    setSyncModal(false);
    setSyncing(true);

    const MIN_DURATION = 6000; // 6 seconds
    const startTime = Date.now();

    try {
      const response = await fetch(
        `${serverHost}/hrms/sync_directory/${directory.dir_uuid}?site_uuid=${siteId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
        }
      );

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result.error || "Sync failed");
      }

    } catch (err) {
      console.error(err);
      alert(err.message || "Sync failed.");
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(MIN_DURATION - elapsed, 0);

      setTimeout(() => {
        setSyncing(false);
      }, remaining);
    }
  };

  const getDeviceImage = (model) => {
    if (!model) return UNKNOWN_DEVICE_SVG;

    const m = model.toLowerCase();

    if (m.includes("verso")) {
      return require("assets/img/devices/2nipverso.jpg");
    }

    if (m.includes("style")) {
      return require("assets/img/devices/2nipstyle.png");
    }

    return UNKNOWN_DEVICE_SVG;
  };

  const selectRow = {
    mode: "checkbox",
    clickToSelect: false,
    selected: selectedDirUuids,
    onSelect: (row, isSelect) => {
      setSelectedDirUuids((prev) =>
        isSelect
          ? [...prev, row.dir_uuid]
          : prev.filter((id) => id !== row.dir_uuid)
      );
    },
    onSelectAll: (isSelect, rows) => {
      setSelectedDirUuids(isSelect ? rows.map((r) => r.dir_uuid) : []);
    },
    classes: "checkbox-column",
    headerClasses: "checkbox-column-header",
  };

  const handleBulkSync = async () => {
    if (!selectedDirUuids.length) return;

    setBulkSyncModal(false);
    setSyncing(true);

    const MIN_DURATION = 6000;
    const startTime = Date.now();

    try {
      const res = await fetch(
        `${serverHost}/hrms/sync_directories?site_uuid=${siteId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({ dir_uuids: selectedDirUuids }),
        }
      );

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Bulk sync failed");

      // Optional: show errors nicely if any
      if (json?.errors?.length) {
        console.warn("Bulk sync errors:", json.errors);
        alert(`Synced with ${json.errors.length} publish error(s). Check console.`);
      }

    } catch (err) {
      console.error(err);
      alert(err.message || "Bulk sync failed.");
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(MIN_DURATION - elapsed, 0);
      setTimeout(() => setSyncing(false), remaining);
      setTimeout(() => setSelectedDirUuids([]), remaining);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedDirUuids.length) return;

    setBulkDeleteModal(false);
    setDeleting(true);

    const MIN_DURATION = 5000;
    const startTime = Date.now();

    try {
      const res = await fetch(
        `${serverHost}/hrms/delete_directories?site_uuid=${siteId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({ dir_uuids: selectedDirUuids }),
        }
      );

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Bulk delete failed");

      // remove deleted dirs + clear selection
      setDirectories((prev) =>
        prev.filter((d) => !selectedDirUuids.includes(d.dir_uuid))
      );
      setSelectedDirUuids([]);

      if (json?.errors?.length) {
        console.warn("Bulk delete publish errors:", json.errors);
        alert(`Deleted, but ${json.errors.length} publish error(s) occurred. Check console.`);
      }

    } catch (err) {
      console.error(err);
      alert(err.message || "Bulk delete failed.");
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(MIN_DURATION - elapsed, 0);
      setTimeout(() => setDeleting(false), remaining);
      setTimeout(() => setSelectedDirUuids([]), remaining);
    }
  };

  const columns = [
    {
      dataField: "dir_name",
      text: "Directory",
      sort: true,
      formatter: (cell) => (
        <Media className="align-items-center">
          <a
            className="avatar rounded-circle mr-3"
            href="#pablo"
            onClick={(e) => e.preventDefault()}
          >
            <img
              alt="Directory"
              src={require("assets/img/theme/directory-icon.png")}
            />
          </a>
          <Media>
            <span className="name mb-0 text-sm">{cell}</span>
          </Media>
        </Media>
      ),
    },
    {
      dataField: "tenant_count",
      text: "Tenants",
      sort: true,
      formatter: (cell) => (
        <span className="badge badge-lg tenant-badge">
          {cell}
        </span>
      ),
    },
    {
      dataField: "devices",
      text: "Enterphones",
      formatter: (devices, row) =>
        devices.length === 0 ? (
          <span className="mb-0">None</span>
        ) : (
          <div className="avatar-group">
            {devices.slice(0, 4).map((device) => (
              <React.Fragment key={device.device_uuid}>
                <a
                  className="avatar avatar-sm rounded-circle"
                  href="#pablo"
                  onClick={(e) => e.preventDefault()}
                  id={`tooltip-device-${row.dir_uuid}-${device.device_uuid}`}
                >
                  <img
                    alt={device.device_name}
                    src={getDeviceImage(device.device_model)}
                  />
                </a>
                <UncontrolledTooltip
                  delay={0}
                  target={`tooltip-device-${row.dir_uuid}-${device.device_uuid}`}
                >
                  {device.device_name}
                </UncontrolledTooltip>
              </React.Fragment>
            ))}
            {devices.length > 4 && (
              <span className="avatar avatar-sm rounded-circle bg-light text-muted d-inline-flex align-items-center justify-content-center">
                +{devices.length - 4}
              </span>
            )}
          </div>
        ),
    },

    {
      dataField: "actions",
      text: "Actions",
      formatter: (_, row) => {
        if (!canPerformActions) return null;

        return (
          <div className="table-actions d-flex gap-2">

            {/* Sync */}
            {/* <Button
              size="sm"
              color="primary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                if (!row.devices || row.devices.length === 0) {
                  alert("This directory has no associated devices.");
                  return;
                }

                toggleSyncModal(row);
              }}
            >
              <i className="fas fa-sync-alt mr-1" />
              Sync
            </Button> */}

            {/* Delete */}
            <Button
              size="sm"
              color="danger"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openDeleteModal(row.dir_uuid);
              }}
            >
              Delete
            </Button>

          </div>
        );
      },
    },
  ];

  return (
    <>
      <HusariaHeader name="Directories" parentName="HRMS" padding="4.5rem" />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card>
              <ToolkitProvider
                keyField="dir_uuid"
                data={directories}
                columns={columns}
                search
              >
                {(props) => (
                  <>
                    <CardHeader
                      className="border-0 header-theme" style={{
                        backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                        color: isDark ? "#e2e8f0" : "#111",
                      }}>
                      <Row className="align-items-center">
                        <Col xs="6" className="d-flex align-items-center">
                          {/* Search */}
                          <Form className="navbar-search navbar-search-light form-inline mr-sm-3">
                            <FormGroup className="mb-0">
                              <InputGroup className="input-group-alternative input-group-merge search-border">
                                <InputGroupText>
                                  <i
                                    className="fas fa-search"
                                    style={{
                                      color: isDark ? "#b0b0b0" : "rgb(102, 102, 102)",
                                    }}
                                  />
                                </InputGroupText>

                                {/* Custom input, NOT toolkit SearchBar */}
                                <input
                                  type="text"
                                  className="form-control table-search-bar"
                                  placeholder="Search directories"
                                  onChange={(e) =>
                                    props.searchProps.onSearch(e.target.value)
                                  }
                                />
                              </InputGroup>
                            </FormGroup>
                          </Form>
                        </Col>

                        <Col
                          xs="6"
                          className="d-flex justify-content-end align-items-center"
                        >
                          <div className="d-flex justify-content-end align-items-center" style={{ gap: "8px" }}>
                            {/* Bulk buttons only when selected */}
                            {selectedDirUuids.length > 0 && (
                              <>
                                {/* <Button
                                  className="btn-round btn-icon"
                                  color="primary"
                                  size="sm"
                                  onClick={() => setBulkSyncModal(true)}
                                >
                                  <span className="btn-inner--icon mr-1">
                                    <i className="fas fa-sync-alt" />
                                  </span>
                                  <span className="btn-inner--text">
                                    Sync Selected ({selectedDirUuids.length})
                                  </span>
                                </Button> */}

                                {canDelete && (
                                  <Button
                                    className="btn-round btn-icon"
                                    color="danger"
                                    size="sm"
                                    onClick={() => setBulkDeleteModal(true)}
                                  >
                                    <span className="btn-inner--icon mr-1">
                                      <i className="fas fa-trash" />
                                    </span>
                                    <span className="btn-inner--text">
                                      Delete Selected ({selectedDirUuids.length})
                                    </span>
                                  </Button>
                                )}
                              </>
                            )}

                            {/* New button */}
                            {hasPermission(userData, PERMISSIONS.CREATE) && (
                              <>
                                <Button
                                  className="btn-round btn-icon"
                                  color="info"
                                  id="tooltip-new-directory"
                                  onClick={() => navigate("/admin/hrms/add/directory")}
                                  size="sm"
                                >
                                  <span className="btn-inner--icon mr-1">
                                    <i className="fas fa-plus" />
                                  </span>
                                  <span className="btn-inner--text">New</span>
                                </Button>

                                <UncontrolledTooltip delay={0} target="tooltip-new-directory">
                                  Add New Directory
                                </UncontrolledTooltip>
                              </>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </CardHeader>

                    <div className="py-4 table-responsive">
                      <BootstrapTable
                        {...props.baseProps}
                        bootstrap4
                        bordered={false}
                        hover
                        responsive
                        pagination={pagination}
                        rowStyle={{ cursor: canEdit ? "pointer" : "default" }}
                        selectRow={selectRow}
                        rowEvents={{
                          onClick: (e, row) => {
                            if (!canEdit) return;
                            if (e.target?.closest("button") || e.target?.closest("a")) return;
                            navigate(`/admin/hrms/edit/directory/${row.dir_uuid}`);
                          },
                        }}
                        classes="align-items-center table-flush"
                        noDataIndication={() =>
                          loading ? (
                            <div className="text-center">Loading...</div>
                          ) : (
                            <div className="text-center">No directories found.</div>
                          )
                        }
                      />
                    </div>
                  </>
                )}
              </ToolkitProvider>

            </Card>
          </div>
        </Row>
      </Container>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={closeDeleteModal} centered>
        <ModalHeader style={{
          backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
          color: isDark ? "#e2e8f0" : "#111",
        }} toggle={closeDeleteModal}>Delete Directory</ModalHeader>
        <ModalBody className="card-body">
          Are you sure you want to delete this directory?
          <br />
          <strong>This will permanently remove it from all associated enterphones.</strong>
        </ModalBody>
        <ModalFooter className="card-body">
          <Button color="light" onClick={closeDeleteModal}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDelete}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>

      {/* Bulk Sync Confirmation */}
      <Modal isOpen={bulkSyncModal} toggle={() => setBulkSyncModal(false)} centered>
        <ModalHeader
          style={{
            backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
            color: isDark ? "#e2e8f0" : "#111",
          }}
          toggle={() => setBulkSyncModal(false)}
        >
          Sync Directories
        </ModalHeader>
        <ModalBody className="card-body">
          Sync <strong>{selectedDirUuids.length}</strong> selected directories?
          <br />
          <strong>This will publish updates to affected enterphones.</strong>
        </ModalBody>
        <ModalFooter className="card-body">
          <Button color="light" onClick={() => setBulkSyncModal(false)}>
            Cancel
          </Button>
          {/* <Button color="primary" onClick={handleBulkSync}>
            Sync
          </Button> */}
        </ModalFooter>
      </Modal>

      {/* Bulk Delete Confirmation */}
      <Modal isOpen={bulkDeleteModal} toggle={() => setBulkDeleteModal(false)} centered>
        <ModalHeader
          style={{
            backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
            color: isDark ? "#e2e8f0" : "#111",
          }}
          toggle={() => setBulkDeleteModal(false)}
        >
          Delete Directories
        </ModalHeader>
        <ModalBody className="card-body">
          Delete <strong>{selectedDirUuids.length}</strong> selected directories?
          <br />
          <strong>This action cannot be undone.</strong>
        </ModalBody>
        <ModalFooter className="card-body">
          <Button color="light" onClick={() => setBulkDeleteModal(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleBulkDelete}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>

      <SyncDeviceModal
        isOpen={syncModal}
        directoryName={directoryToSync?.dir_name}
        onCancel={() => setSyncModal(false)}
        onConfirm={() => handleSync(directoryToSync)}
      />
      {syncing && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: isDark ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.7)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Card
            style={{
              padding: "1.5rem 2rem",
              borderRadius: "1rem",
              backgroundColor: isDark ? "#2a2a2a" : "#ffffff",
              color: isDark ? "#f8f9fa" : "#111827",
              border: isDark ? "1px solid #3a3a3a" : "1px solid #dee2e6",
              textAlign: "center",
              minWidth: 260,
            }}
          >
            <Spinner animation="border" variant={isDark ? "light" : "primary"} />
            <div className="mt-3" style={{ fontWeight: 600 }}>
              {syncLabel || "Syncing devices…"}
            </div>
            <div style={{ opacity: 0.8, fontSize: "0.9rem" }}>
              Please do not close this page.
            </div>
          </Card>
        </div>
      )}
      {deleting && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: isDark ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.7)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Card
            style={{
              padding: "1.5rem 2rem",
              borderRadius: "1rem",
              backgroundColor: isDark ? "#2a2a2a" : "#ffffff",
              color: isDark ? "#f8f9fa" : "#111827",
              border: isDark ? "1px solid #3a3a3a" : "1px solid #dee2e6",
              textAlign: "center",
              minWidth: 260,
            }}
          >
            <Spinner animation="border" variant={isDark ? "light" : "primary"} />
            <div className="mt-3" style={{ fontWeight: 600 }}>
              {deleteLabel || "Deleting directory…"}
            </div>
            <div style={{ opacity: 0.8, fontSize: "0.9rem" }}>
              Please do not close this page.
            </div>
          </Card>
        </div>
      )}
    </>
  );
}

export default Directories;
